import './AvailableBotsForSit.styles.scss';

import { AxiosResponse } from 'axios';
import { Avatar } from 'primereact/avatar';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Card } from 'primereact/card';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { useDebounce } from 'primereact/hooks';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

import { getPokerRoom, REFRESH_BOTS_FOR_SEAT_INTERVAL } from '../../constants';
import { AvailableBotForSitResponse } from '../../model/availablebot/AvailableBotForSitResponse';
import { GameType } from '../../model/enumeration/GameType.enum';
import { Permission } from '../../model/enumeration/Permission.enum';
import { PokerRoom } from '../../model/enumeration/PokerRoom';
import { SortDirection } from '../../model/SortDirection';
import authStore from '../../store/AuthStore';
import availableBotStore from '../../store/AvailableBotStore';
import botManagementStore from '../../store/BotManagementStore';
import botNoteStore from '../../store/BotNoteStore';
import commonStore from '../../store/CommonStore';
import notificationStore from '../../store/NotificationStore';
import permissionStore from '../../store/PermissionStore';
import tableStore from '../../store/TableStore';
import userSettingsStore from '../../store/UserSettingsStore';
import waitListStore from '../../store/WaitListStore';
import { includesInObjectValues } from '../../utils/common.utils';
import {
  dateFromString,
  formatInstantTo24HourTime,
} from '../../utils/date.utils';
import { getBuyInConfirmDialog } from '../AvailableBotForManageParams/availableBot.render.utils';
import AvailableBotsForSitWithClub from '../AvailableBotsForSitWithClub/AvailableBotsForSitWithClub';
import CountryFlag from '../CountryFlag/CountryFlag';
import AvailableBotForJoinTableByLink from '../Dashboard/AvailableBotForJoinTableByLink/AvailableBotForJoinTableByLink';
import PlayerColorSettings from '../Player/PlayerColorSettings/PlayerColorSettings';
import SortingInput from '../shared/SortingInput/SortingInput';
import {
  AvailableBotsForSitProps,
  AvailableBotsSortingOptions,
} from './AvailableBotsForSit.types';
import SeatPlacePicker from './SeatPlacePicker/SeatPlacePicker';

const AvailableBotsForSeat: FC<AvailableBotsForSitProps> = ({
  tableId,
  room,
  gameType,
  limit,
  union,
  isVisible,
  onHide,
  place,
  isTableFull,
  tablePlayerCount,
  fromLobbyTable,
}) => {
  const [availableBots, setAvailableBots] = useState<
    AvailableBotForSitResponse[]
  >([]);

  const [filteredBots, setFilteredBots] = useState<
    AvailableBotForSitResponse[]
  >([]);

  const [isJoinByLinkVisible, setJoinByLinkVisible] = useState<boolean>(false);

  const [showMoreMap, setShowMoreMap] = useState<Map<string, boolean>>(
    new Map(),
  );

  const [sitTimeMap, setSitTimeMap] = useState<Map<string, Date>>(new Map());
  const [seatPlaceMap, setSeatPlaceMap] = useState<Map<string, number | null>>(
    new Map(),
  );

  const [botsToJoin, setBotsToJoin] = useState<string[]>([]);
  const [joinAllBotsCheckbox, setJoinAllBotsCheckbox] =
    useState<boolean>(false);

  const [selectedClubs, setSelectedClubs] = useState<string[]>([]);

  const buyInRef = useRef<number | null>(null);

  const calendarRefs = useRef<Record<string, Calendar | null>>({});

  const [searchValue, debouncedSearchValue, setSearchValue] = useDebounce(
    '',
    300,
  );

  const [selectedSortValue, setSelectedSortValue] =
    useState<AvailableBotsSortingOptions>();
  const [sortDirection, setSortDirection] = useState<SortDirection>();

  const [updatedNote, setUpdatedNote] = useState<string>('');
  const [updatedPidNote, setUpdatedPidNote] = useState<string>('');

  const sortedBots = useMemo(() => {
    if (!selectedSortValue) {
      return filteredBots;
    } else {
      return filteredBots.slice().sort((b1, b2) => {
        if (selectedSortValue === AvailableBotsSortingOptions.OPENED_TABLES) {
          const difference = b1.openedTables - b2.openedTables;
          return sortDirection === SortDirection.ASC ? difference : -difference;
        }
      });
    }
  }, [filteredBots, selectedSortValue, sortDirection]);

  const hasShowUniqueIdsPermission = useMemo(
    () => permissionStore.hasPermission(Permission.SHOW_UNIQUE_IDS),
    [authStore.authenticatedUser],
  );

  const hasChangeBotsClubsPermission = useMemo(
    () => permissionStore.hasPermission(Permission.CHANGE_BOTS_CLUBS),
    [authStore.authenticatedUser],
  );

  const isDisabledBotForSit = (bot: AvailableBotForSitResponse): boolean =>
    (bot.sitTime != null && !sitTimeMap.has(bot.pid)) ||
    (bot.sitTime &&
      new Date(bot.sitTime).getTime() === sitTimeMap.get(bot.pid)?.getTime());

  const allAvailableBotsForSeat = useMemo(
    () =>
      sortedBots.filter(bot => !isDisabledBotForSit(bot)).map(bot => bot.pid),
    [sortedBots],
  );

  useEffect(() => {
    setJoinAllBotsCheckbox(
      allAvailableBotsForSeat.length === botsToJoin.length,
    );
  }, [allAvailableBotsForSeat.length, botsToJoin.length]);

  const isMtt = useMemo(() => gameType.includes(GameType.MTT), [gameType]);

  const tableIdForSit = useMemo(
    () => (isMtt && !fromLobbyTable ? tableId.replace(/-\d+$/, '') : tableId),
    [isMtt, fromLobbyTable, tableId],
  );

  const loadAvailableBots = (): void => {
    if (isVisible) {
      availableBotStore
        .getAvailableBotsForSit(tableIdForSit, room, gameType, limit, union)
        .then(bots => {
          setAvailableBots(bots);
          const botsIds = new Set(bots.map(bot => bot.pid));
          setShowMoreMap(
            prev => new Map([...prev].filter(([key]) => botsIds.has(key))),
          );
          setBotsToJoin(prev => [...prev.filter(pid => botsIds.has(pid))]);
        });
    } else {
      setShowMoreMap(new Map());
    }
  };

  useEffect(() => {
    if (isVisible) {
      loadAvailableBots();
      const interval = setInterval(
        loadAvailableBots,
        REFRESH_BOTS_FOR_SEAT_INTERVAL,
      );
      return () => clearInterval(interval);
    } else {
      setSitTimeMap(new Map());
      setSeatPlaceMap(new Map());
      setShowMoreMap(new Map());
      setBotsToJoin([]);
      setAvailableBots([]);
      setFilteredBots([]);
      setSearchValue('');
    }
  }, [isVisible, tableId, tableIdForSit, room, gameType, limit, union]);

  useEffect(() => {
    setFilteredBots(
      availableBots.filter(bot =>
        includesInObjectValues(bot, debouncedSearchValue),
      ),
    );
  }, [debouncedSearchValue, availableBots]);

  useEffect(() => {
    if (!isVisible) {
      setSelectedClubs([]);
      return;
    }
    if (fromLobbyTable) {
      setSelectedClubs(
        tableStore.unionNotNullAndNotZeroValue(union.club) ? [union.club] : [],
      );
    } else {
      tableStore
        .findActiveTableById(tableId)
        .then(table => setSelectedClubs(tableStore.getClubsByTable(table)));
    }
  }, [tableId, isVisible]);

  const isPokerNow = useMemo(() => getPokerRoom() === PokerRoom.PokerNow, []);

  const isClubGG = useMemo(() => getPokerRoom() === PokerRoom.ClubGG, []);

  const isPokerBros = useMemo(() => getPokerRoom() === PokerRoom.PokerBros, []);

  const isXpoker = useMemo(() => getPokerRoom() === PokerRoom.Xpoker, []);

  const setShowMore = (pid: string): void => {
    setShowMoreMap(
      prevState => new Map([...prevState, [pid, !prevState.get(pid)]]),
    );
  };

  const getJoinPlace = (pid: string): number | undefined => {
    if (seatPlaceMap.has(pid)) {
      return seatPlaceMap.get(pid);
    }
    return place;
  };

  const join = (pid: string, buyIn: number | null): void => {
    let apiPromise: Promise<AxiosResponse<void>>;

    const joinPlace = isMtt ? null : getJoinPlace(pid);
    const sitTime = sitTimeMap.get(pid);
    if (sitTime) {
      apiPromise = botManagementStore.scheduleBotSitting(
        tableId,
        gameType,
        room,
        pid,
        joinPlace,
        sitTime.toISOString(),
        buyIn,
      );
    } else if (isTableFull && !isMtt) {
      apiPromise = waitListStore.addBotToWaitList(
        tableId,
        gameType,
        pid,
        buyIn,
      );
    } else {
      apiPromise = botManagementStore.forceJoinTable(
        tableId,
        gameType,
        room,
        pid,
        joinPlace,
        buyIn,
      );
    }
    apiPromise
      .then(() => {
        notificationStore.showNotification({
          severity: 'success',
          summary: 'Success',
          detail: (
            <>
              Request sent to the bot (<b>{pid}</b>) to join the table{' '}
              {isTableFull && !sitTime && !isMtt ? 'waitlist' : ''} (
              <b>{tableId}</b>){' '}
              {sitTime ? (
                <>
                  at <b>{formatInstantTo24HourTime(sitTime.toISOString())}</b>
                  {joinPlace != null && !isMtt ? (
                    <>
                      {' '}
                      on place <b>{joinPlace}</b>
                    </>
                  ) : (
                    ''
                  )}
                </>
              ) : (
                ''
              )}
              <>
                {joinPlace != null && !isTableFull && !sitTime && !isMtt ? (
                  <span>
                    on place <b>{joinPlace}</b>
                  </span>
                ) : (
                  <></>
                )}
              </>
            </>
          ),
        });
      })
      .catch(response => {
        notificationStore.showNotification({
          severity: 'error',
          summary: response.response.data.error || 'Error',
          detail: 'Error while sending request to the bot',
        });
      })
      .finally(loadAvailableBots);
  };

  const onJoin = (pid: string): void => {
    if (isPokerNow) {
      setJoinByLinkVisible(true);
      return;
    }
    if (!userSettingsStore.settings.table.askBuyInBeforeSit || isMtt) {
      return join(pid, null);
    }

    getBuyInConfirmDialog(buyInRef, () => join(pid, buyInRef.current));
  };

  const batchJoinNotification = (
    apiPromise: Promise<AxiosResponse<void, any>>,
    message: string,
  ): void => {
    apiPromise
      .then(() => {
        notificationStore.showNotification({
          severity: 'success',
          summary: 'Success',
          detail: (
            <>
              Request sent to the bots (<b>{message}</b>) to join the table (
              <b>{tableId}</b>){' '}
            </>
          ),
        });
        loadAvailableBots();
        setBotsToJoin([]);
        setJoinAllBotsCheckbox(false);
      })
      .catch(response => {
        notificationStore.showNotification({
          severity: 'error',
          summary: response.response.data.error || 'Error',
          detail: 'Error while sending request to the bots',
        });
      });
  };

  const onBatchJoin = (): void => {
    const botsForceJoin: { pid: string }[] = [];
    const botsScheduledSit: { pid: string; sitTime: string }[] = [];

    botsToJoin.forEach(pid => {
      if (sitTimeMap.has(pid)) {
        botsScheduledSit.push({
          pid,
          sitTime: sitTimeMap.get(pid).toISOString(),
        });
      } else {
        botsForceJoin.push({ pid });
      }
    });

    if (botsForceJoin.length > 0) {
      batchJoinNotification(
        botManagementStore.batchForceJoinTable(
          tableId,
          room,
          gameType,
          botsForceJoin,
        ),
        botsForceJoin.map(bot => bot.pid).join(', '),
      );
    }

    if (botsScheduledSit.length > 0) {
      batchJoinNotification(
        botManagementStore.batchScheduleBotsSitting(
          tableId,
          room,
          gameType,
          botsScheduledSit,
        ),
        botsScheduledSit
          .map(bot => `${bot.pid} at ${formatInstantTo24HourTime(bot.sitTime)}`)
          .join(', '),
      );
    }
  };

  const updateCalendarInputValue = (pid: string, value: Date): void => {
    const botRef = calendarRefs.current[pid];
    if (botRef) {
      setTimeout(() => {
        (botRef.getInput() as any).value = value.toLocaleTimeString('ru-RU', {
          hour: '2-digit',
          minute: '2-digit',
        });
      }, 1);
    }
  };

  const onSitTimeUpdate = (
    bot: AvailableBotForSitResponse,
    sitTime: Date | null,
  ): void => {
    setSitTimeMap(prevState => {
      if (sitTime) {
        const botSitTime = new Date(sitTime);
        botSitTime.setSeconds(0, 0);

        const prevTime = prevState.get(bot.pid);

        if (prevTime) {
          const prevMinutes = prevTime.getMinutes();
          const newMinutes = botSitTime.getMinutes();

          if (prevMinutes === 59 && newMinutes === 0) {
            botSitTime.setHours(botSitTime.getHours() + 1);
          } else if (prevMinutes === 0 && newMinutes === 59) {
            botSitTime.setHours(botSitTime.getHours() - 1);
          }
        }

        const now = new Date();
        if (botSitTime.getTime() < now.getTime()) {
          updateCalendarInputValue(bot.pid, now);
          return new Map([...prevState, [bot.pid, now]]);
        } else {
          updateCalendarInputValue(bot.pid, botSitTime);
          return new Map([...prevState, [bot.pid, botSitTime]]);
        }
      } else {
        const newMap = new Map(prevState);
        newMap.delete(bot.pid);
        return newMap;
      }
    });
  };

  const onClearSitTime = (bot: AvailableBotForSitResponse): void => {
    if (bot.sitTime) {
      botManagementStore
        .cancelScheduledBotSitting(tableId, room, bot.pid)
        .then(() => {
          onSitTimeUpdate(bot, null);
          loadAvailableBots();
          notificationStore.showNotification({
            severity: 'success',
            summary: 'Success',
            detail: (
              <>
                Request for the bot to join the table at{' '}
                <b>{formatInstantTo24HourTime(bot.sitTime)}</b> was cancelled
              </>
            ),
          });
        });
    } else {
      onSitTimeUpdate(bot, null);
    }
  };

  const getSitTime = (bot: AvailableBotForSitResponse): Date => {
    if (sitTimeMap.has(bot.pid)) {
      return sitTimeMap.get(bot.pid);
    } else if (bot.sitTime) {
      return dateFromString(bot.sitTime);
    } else {
      return undefined;
    }
  };

  const isSeatPlaceAvailable = (isClubGG || isPokerBros || isXpoker) && !isMtt;

  const onSeatPlaceUpdate = (
    bot: AvailableBotForSitResponse,
    seatPlace: number | null,
  ): void => {
    setSeatPlaceMap(prevState => {
      return new Map([...prevState, [bot.pid, seatPlace]]);
    });
  };

  const onClearSeatPlace = (bot: AvailableBotForSitResponse): void =>
    onSeatPlaceUpdate(bot, null);

  const getSeatPlaceValue = (
    bot: AvailableBotForSitResponse,
  ): number | null => {
    if (seatPlaceMap.has(bot.pid)) {
      return seatPlaceMap.get(bot.pid);
    } else if (bot.place !== null) {
      return bot.place;
    }
    return place ?? null;
  };

  const getSeatPlaceDefault = (
    bot: AvailableBotForSitResponse,
  ): number | null => (seatPlaceMap.has(bot.pid) ? null : place ?? null);

  const onBatchBotJoinCheckbox = (pid: string): void =>
    setBotsToJoin(prev => {
      if (prev.includes(pid)) {
        setJoinAllBotsCheckbox(false);
        return prev.filter(id => id !== pid);
      }
      if (allAvailableBotsForSeat.length === prev.length + 1) {
        setJoinAllBotsCheckbox(true);
      }
      return [...prev, pid];
    });

  const onBatchAllBotsJoin = (): void =>
    setBotsToJoin(prev => {
      if (prev.length === allAvailableBotsForSeat.length) {
        setJoinAllBotsCheckbox(false);
        return [];
      }
      setJoinAllBotsCheckbox(true);
      return [...allAvailableBotsForSeat];
    });

  const getJoinBtnLabel = (bot: AvailableBotForSitResponse): string => {
    if (sitTimeMap.has(bot.pid) && !isPokerNow) {
      return 'Schedule';
    } else if (isTableFull && !isMtt && !isPokerNow) {
      return 'Waitlist';
    } else {
      return 'Join';
    }
  };

  const onUpdateBotNote = (pid: string, note: string): void => {
    setUpdatedPidNote(pid);
    setUpdatedNote(note);
  };

  const onSaveBotNote = (bot: AvailableBotForSitResponse): void => {
    if (bot.pid !== updatedPidNote || bot.note === updatedNote) {
      return;
    }

    botNoteStore
      .updateBotNote(updatedPidNote, updatedNote)
      .then(response => response.data)
      .then(note => {
        bot.note = note;
        setUpdatedNote('');
        setUpdatedPidNote('');
        notificationStore.showNotification({
          severity: 'success',
          summary: 'Success',
          detail: `Note has been successfully set for the bot ${bot.pid}`,
        });
      });
  };

  const header = (
    <div className="join-bots-header">
      <div className="text-center">JOIN BOTS</div>
      <IconField className="mt-4" iconPosition="left">
        <InputIcon size={40} className="pi pi-search" />
        <InputText
          v-model="value1"
          value={searchValue}
          onChange={e => setSearchValue(e.target.value)}
          placeholder="Search"
        />
      </IconField>
    </div>
  );

  const footer = useMemo(
    () =>
      hasChangeBotsClubsPermission &&
      isVisible && (
        <AvailableBotsForSitWithClub
          tableId={tableIdForSit}
          selectedClubs={selectedClubs}
          excludedPids={availableBots.map(bot => bot.pid)}
        />
      ),
    [
      selectedClubs,
      availableBots,
      isVisible,
      hasChangeBotsClubsPermission,
      tableIdForSit,
    ],
  );

  return (
    <Dialog
      className="AvailableBotsForSit-Component"
      header={header}
      visible={isVisible}
      onHide={onHide}
      onClick={e => e.stopPropagation()}
      data-ignore-outside-click
      maskClassName="ignore-outside-click"
      footer={footer}
      style={{
        height: footer ? '930px' : '750px',
        width: isSeatPlaceAvailable ? '1040px' : '880px',
      }}
    >
      <SortingInput
        className="mb-3"
        sessionStorageKey="available-bots-for-seat-sorting"
        sortingOptionsEnum={AvailableBotsSortingOptions}
        selectedSortProperty={selectedSortValue}
        sortDirection={sortDirection}
        onSortDirectionChanged={setSortDirection}
        onSelectedSortPropertyChanged={value =>
          setSelectedSortValue(value as AvailableBotsSortingOptions)
        }
      />
      {isMtt && sortedBots.length > 0 && (
        <div className="batch-join-bots">
          <div className="batch-join-bots-checkbox">
            <Checkbox
              inputId="batch-join-bots-checkbox"
              name="batch-join-bots-checkbox"
              value="batch-join-bots-checkbox"
              onChange={onBatchAllBotsJoin}
              checked={joinAllBotsCheckbox}
            />
            <label
              htmlFor="batch-join-bots-checkbox"
              className="batch-join-bots-label"
            >
              Select all bots
            </label>
          </div>
          <Button
            className="col-2 join-btn"
            label="Join all"
            severity="success"
            disabled={botsToJoin.length === 0}
            onClick={onBatchJoin}
          />
        </div>
      )}
      {sortedBots.map(bot => (
        <div className="available-bot-wrapper" key={bot.pid}>
          {isMtt && (
            <Checkbox
              inputId={`bot-${bot.pid}-checkbox`}
              name={`bot-${bot.pid}-checkbox`}
              value={`bot-${bot.pid}-checkbox`}
              onChange={() => onBatchBotJoinCheckbox(bot.pid)}
              checked={botsToJoin.includes(bot.pid)}
              disabled={isDisabledBotForSit(bot)}
            />
          )}
          <Card
            className="bots-wrapper position-relative"
            pt={{
              content: {
                className:
                  'row justify-content-between align-items-center w-100 position-relative',
              },
            }}
          >
            <PlayerColorSettings pid={bot.pid} />
            <div className="col-1 bot-avatar">
              {bot.country && (
                <div className="country">
                  <CountryFlag countryCode={bot.country} />
                </div>
              )}
              <Avatar
                className="justify-content-center bot-avatar pe-0"
                shape="circle"
                size="xlarge"
                image="/assets/bot.svg"
              />
            </div>
            <div className="col-5">
              <div className="info-field">
                <b>Pid:</b>
                {` ${bot.pid}`}
              </div>
              <div className="info-field">
                <b>Nick:</b>
                {` ${bot.nick}`}
              </div>
              {hasShowUniqueIdsPermission && (
                <div className="info-field unique-id">
                  <b>Unique ID:</b>
                  {` ${bot.uniqueId}`}
                </div>
              )}
              {showMoreMap.get(bot.pid) && (
                <>
                  {bot.agent && (
                    <div className="info-field">
                      <b>Agent:</b>
                      {` ${bot.agent}`}
                    </div>
                  )}
                  {bot.superLeague && (
                    <div className="info-field">
                      <b>Super League:</b>
                      {` ${bot.superLeague}`}
                    </div>
                  )}
                  {bot.league && (
                    <div className="info-field">
                      <b>League:</b>
                      {` ${bot.league}`}
                    </div>
                  )}
                  {bot.club && (
                    <div className="info-field">
                      <b>Club:</b>
                      {` ${bot.club}`}
                    </div>
                  )}
                  <div className="info-field">
                    <b>Limits:</b>
                    {` ${
                      bot.limits
                        ? commonStore.convertLimits(bot.limits).join(', ')
                        : 'ALL'
                    }`}
                  </div>
                  <div className="info-field">
                    <b>Game types:</b>
                    {` ${bot.gameTypes ? bot.gameTypes.join(', ') : 'ALL'}`}
                  </div>
                  <div className="info-field">
                    <b>Balance:</b>
                    {` ${bot.balance}`}
                  </div>
                  {bot.prStats?.vpip && (
                    <div className="info-field">
                      <b>VPIP:</b>
                      {` ${bot.prStats.vpip}`}
                    </div>
                  )}
                  {bot.prStats?.pfr && (
                    <div className="info-field">
                      <b>PFR:</b>
                      {` ${bot.prStats.pfr}`}
                    </div>
                  )}
                  {bot.tournamentTableIds?.length && (
                    <div className="info-field">
                      <b>Tournament table ids:</b>
                      {` ${bot.tournamentTableIds.join(', ')}`}
                    </div>
                  )}
                </>
              )}
              <Button
                className="show-more-btn p-0 mt-1"
                label={showMoreMap.get(bot.pid) ? 'Less' : 'Show more'}
                link
                onClick={() => setShowMore(bot.pid)}
              />
            </div>
            <div className="col-1 opened-tables">
              {bot.openedTables}/{bot.openTableLimit}
            </div>
            {isSeatPlaceAvailable && (
              <SeatPlacePicker
                value={getSeatPlaceValue(bot)}
                defaultPlace={getSeatPlaceDefault(bot)}
                seatCount={(tablePlayerCount || 1) - 1}
                disabled={
                  isDisabledBotForSit(bot) ||
                  (isTableFull && !sitTimeMap.has(bot.pid))
                }
                onChange={seat => onSeatPlaceUpdate(bot, seat)}
                onClear={() => onClearSeatPlace(bot)}
              />
            )}
            {!isPokerNow && (
              <Calendar
                ref={el => (calendarRefs.current[bot.pid] = el)}
                className="sit-time-calendar col-1"
                inputClassName="text-center"
                placeholder="Time"
                value={getSitTime(bot)}
                onChange={e => onSitTimeUpdate(bot, e.value)}
                onClearButtonClick={() => onClearSitTime(bot)}
                timeOnly
                hourFormat="24"
                selectionMode="single"
                stepMinute={1}
                showButtonBar
                todayButtonClassName="d-none"
              />
            )}
            <div className="col-2 position-relative join-wrapper">
              <Button
                className="join-btn w-100"
                label={getJoinBtnLabel(bot)}
                severity="success"
                disabled={
                  isDisabledBotForSit(bot) || (isMtt && botsToJoin.length > 0)
                }
                onClick={() => onJoin(bot.pid)}
              />
              <div className="bot-note">
                <InputText
                  placeholder="Note"
                  value={
                    (updatedPidNote === bot.pid ? updatedNote : bot.note) ?? ''
                  }
                  onChange={e => onUpdateBotNote(bot.pid, e.target.value)}
                  onBlur={() => onSaveBotNote(bot)}
                />
              </div>
            </div>
            {isPokerNow && (
              <AvailableBotForJoinTableByLink
                bot={bot}
                isVisible={isJoinByLinkVisible}
                onHide={() => setJoinByLinkVisible(false)}
                doOnSuccess={() => loadAvailableBots()}
              />
            )}
          </Card>
        </div>
      ))}
    </Dialog>
  );
};

export default AvailableBotsForSeat;
